import { Panel } from '@app/components/common/Collapse/Collapse';
import { IDealRoomSectionContent } from '@app/interfaces/deal-room';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from '../DealRoomLanding.styles';
import { Comparison } from './Comparison/Comparison';
import { useSubMenuMouseflow } from '@app/hooks/useSubMenuMouseflow';
import { scrollToSection } from '@app/utils/scrollToSection';

interface ComparisonsProps {
  section: IDealRoomSectionContent;
}

/**
 * The comparisons parent: one panel per comparison, headed by its two insurers.
 * Mirrors `Document`, which does the same for the deal room's files.
 */
export const Comparisons: React.FC<ComparisonsProps> = ({ section }) => {
  const { t } = useTranslation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  // The Mouseflow hook reports one section at a time. Kept empty until the
  // reader actually opens something, so the panels that start expanded are not
  // reported as opens they performed.
  const [lastOpened, setLastOpened] = useState<string>('');
  const seededRef = useRef(false);

  useSubMenuMouseflow(lastOpened, (key) => {
    const sub = section.subMenu?.find((s) => s.key === key);
    return sub?.label || t(sub?.labelKey || key);
  });

  // Every comparison starts expanded. Seeded once, on the first render that
  // actually has children — re-seeding would reopen panels the reader closed.
  useEffect(() => {
    if (seededRef.current || !section.subMenu?.length) return;
    seededRef.current = true;
    setOpenKeys(section.subMenu.map((subSection) => subSection.key));
  }, [section.subMenu]);

  useEffect(() => {
    /**
     * `scroll` only for a hash the reader navigated to. On mount the landing is
     * already being driven by DealRoomLanding, and scrolling from here too
     * turns it into a race the two settle differently on each load.
     */
    const applyHash = (scroll: boolean) => {
      if (!window.location.hash) return;
      const hashKey = window.location.hash.replace('#', '');
      const subMatch = section.subMenu?.find((subSection) => subSection.key === hashKey);
      if (subMatch) {
        setOpenKeys((keys) => (keys.includes(subMatch.key) ? keys : [...keys, subMatch.key]));
        if (scroll) setLastOpened(subMatch.key);
      }
    };

    applyHash(false);
    const onHashChange = () => applyHash(true);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [section.subMenu]);

  useEffect(() => {
    // No delay: scrollToSection re-reads the panel each tick and waits for it
    // to settle, so waiting out the open animation here only postpones the
    // landing and makes it read as a second, separate jump.
    scrollToSection(lastOpened);
  }, [lastOpened]);

  const onChange = (keys: string | string[]) => {
    const next = Array.isArray(keys) ? keys : [keys];
    setLastOpened(next.find((key) => !openKeys.includes(key)) ?? '');
    setOpenKeys(next);
  };

  if (!section.subMenu || section.subMenu.length === 0) {
    return <S.NoData>{t('dealRooms.landing.noComparisons')}</S.NoData>;
  }

  return (
    <S.ChildContainer className="child-container">
      <S.ChildCollapse activeKey={openKeys} onChange={onChange}>
        {section.subMenu.map((subSection) => (
          <Panel
            id={subSection.key}
            header={
              <S.PanelHeaderText>
                <span>{subSection.label || t(subSection.labelKey)}</span>
              </S.PanelHeaderText>
            }
            key={subSection.key}
          >
            <Comparison comparison={subSection.comparison} />
          </Panel>
        ))}
      </S.ChildCollapse>
    </S.ChildContainer>
  );
};
